/**
 * NEOGRIPX Studio — capa de API (backend real en Render, vía App Proxy).
 * -----------------------------------------------------------------------
 * ÚNICO archivo del wizard que conoce:
 *   - las rutas reales del backend,
 *   - los nombres de campo exactos que espera (marca/modelo/anio/estilo/
 *     colores/referencia/intensidad/elementos_imprescindibles/photo),
 *   - y cómo traducir un error HTTP en un mensaje que hable como NEOGRIPX.
 *
 * assets/studio.js NO debe construir peticiones a mano ni conocer estos
 * nombres — todo pasa por window.NXStudioApi. Así el mapeo vive en un solo
 * sitio, no repartido por el código.
 *
 * Nota de nombres: el backend usa "elementos_imprescindibles" (snake_case,
 * así se implementó en el Paso 4 del backend) — no "elementosImprescindibles".
 * Se usa aquí el nombre real para no romper la integración.
 */
(function (global) {
  'use strict';

  var BASE = '/apps/studio'; // relativo: pasa por el App Proxy de la propia tienda, sin CORS

  // -----------------------------------------------------------------
  // Mapeo: estado interno del wizard (assets/studio.js) -> FormData real
  // -----------------------------------------------------------------
  function buildSessionFormData(state) {
    var fd = new FormData();
    fd.append('marca', state.bike.brand || '');
    fd.append('modelo', state.bike.model || '');
    fd.append('anio', state.bike.year || '');
    fd.append('estilo', state.style.choice === 'Otro' ? state.style.otherText : (state.style.choice || ''));
    fd.append('colores', (state.customization.colors || []).join(', '));
    fd.append('referencia', state.customization.reference || '');
    fd.append('intensidad', state.customization.intensity || '');
    fd.append('elementos_imprescindibles', state.customization.musts || '');
    if (state.bike.photoFile) {
      fd.append('photo', state.bike.photoFile, state.bike.photoFile.name || 'foto.jpg');
    }
    return fd;
  }

  function StudioApiError(context, status, body) {
    this.name = 'StudioApiError';
    this.context = context; // 'create-session' | 'generate' | 'poll'
    this.status = status;
    this.body = body;
    this.message = 'StudioApiError: ' + context + ' (' + status + ')';
  }
  StudioApiError.prototype = Object.create(Error.prototype);
  StudioApiError.prototype.constructor = StudioApiError;

  function safeJson(res) {
    return res
      .json()
      .catch(function () {
        return null;
      });
  }

  // -----------------------------------------------------------------
  // Llamadas reales
  // -----------------------------------------------------------------
  function createSession(state) {
    return fetch(BASE + '/sessions', { method: 'POST', body: buildSessionFormData(state) }).then(function (res) {
      return safeJson(res).then(function (body) {
        if (!res.ok) throw new StudioApiError('create-session', res.status, body);
        return body.session_id;
      });
    });
  }

  function generateRound(sessionId) {
    var url = BASE + '/sessions/' + encodeURIComponent(sessionId) + '/generate';
    return fetch(url, { method: 'POST' }).then(function (res) {
      return safeJson(res).then(function (body) {
        if (!res.ok) throw new StudioApiError('generate', res.status, body);
        return body; // { round, status }
      });
    });
  }

  function pollRound(sessionId, round) {
    var url = BASE + '/sessions/' + encodeURIComponent(sessionId) + '/generate/' + round;
    return fetch(url).then(function (res) {
      return safeJson(res).then(function (body) {
        if (!res.ok) throw new StudioApiError('poll', res.status, body);
        return body; // { round, status, concepts? }
      });
    });
  }

  // -----------------------------------------------------------------
  // Mensajes de error orientados al cliente — NUNCA códigos técnicos, IDs
  // internos, stack traces ni texto crudo de BFL. Solo estas frases.
  // -----------------------------------------------------------------
  function describeError(err) {
    if (!(err instanceof StudioApiError)) {
      return 'No se pudo conectar. Comprueba tu conexión e inténtalo de nuevo.';
    }

    var context = err.context;
    var status = err.status;

    if (status === 404) {
      return 'Tu sesión ha caducado. Necesitamos volver a preparar tu diseño.';
    }
    if (context === 'create-session' && status === 429) {
      return 'Has realizado varias pruebas en poco tiempo. Espera un poco antes de crear una nueva sesión.';
    }
    if (context === 'create-session' && status === 400) {
      return 'Revisa la foto que has subido (formato JPG, PNG o WEBP, máximo 10 MB) e inténtalo de nuevo.';
    }
    if (context === 'generate' && status === 429) {
      return 'El estudio de diseño está temporalmente limitado. Inténtalo de nuevo más tarde.';
    }
    if (context === 'generate' && status === 403) {
      return 'Ya se han generado todas las propuestas disponibles para este diseño.';
    }
    if (context === 'generate' && status === 409) {
      return 'Ya hay una generación en curso, espera un momento y vuelve a intentarlo.';
    }
    return 'Algo ha fallado al preparar tu diseño. Inténtalo de nuevo en unos segundos.';
  }

  global.NXStudioApi = {
    createSession: createSession,
    generateRound: generateRound,
    pollRound: pollRound,
    describeError: describeError,
    StudioApiError: StudioApiError
  };
})(window);
